/**
 * 合成进度轮询：当项目处于 composing 状态时，每 5 秒拉取最新 composition。
 *
 * - 仅当 status 为 pending/running 时轮询；completed/failed 立刻停止
 * - 用于工作台头部展示「合成中」徽标
 */

import { useEffect, useState } from 'react';
import { directorApi, type DirectorComposition } from '../services/directorApi';

export interface CompositionPollingState {
  composition: DirectorComposition | null;
  loading: boolean;
}

export function useCompositionPolling(
  projectId: string | undefined,
  options: { enabled?: boolean; intervalMs?: number } = {}
): CompositionPollingState & { reload: () => Promise<void> } {
  const [composition, setComposition] = useState<DirectorComposition | null>(null);
  const [loading, setLoading] = useState(false);
  const intervalMs = options.intervalMs ?? 5000;
  const enabled = options.enabled ?? true;

  const reload = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const list = await directorApi.listCompositions(projectId);
      setComposition(list[0] ?? null);
    } catch {
      // 静默
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId || !enabled) return;
    let cancelled = false;
    let timer: NodeJS.Timeout | null = null;

    const tick = async () => {
      try {
        const list = await directorApi.listCompositions(projectId);
        if (cancelled) return;
        const latest = list[0] ?? null;
        setComposition(latest);

        // 进行中才继续轮询
        if (latest && (latest.status === 'pending' || latest.status === 'running')) {
          timer = setTimeout(tick, intervalMs);
        }
      } catch {
        // 静默重试
        if (!cancelled) timer = setTimeout(tick, intervalMs);
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [projectId, enabled, intervalMs]);

  return { composition, loading, reload };
}
